import { isAuthenticated } from "../lib/auth";

/**
 * FLIGHT EVENT TRACKER
 *
 * Two main patterns for tracking events:
 *
 * PATTERN 1: State-changing events (Update + Send)
 * - Use checkSessionAndUpdateStage() when you need to update user's journey state
 * - Example: User performs search, selects flight, enters customer details
 *
 * PATTERN 2: Simple tracking events (Just Send)
 * - Use getEventTrackerData() when you just need to track an event
 * - Example: Page loads, button clicks, navigation events
 *
 * - Sample usage: check in - HomeSearch.js
 */

const EVENT_TRACKER_STORAGE_KEY = "flymya_event_tracker_data";
const createEmptyStages = () => ({
  searching: {},
  choosing: {},
  customer_detail: {},
  payment: {},
});

const getInitialTrackerData = () => ({
  flights: createEmptyStages(),
  hotels: createEmptyStages(),
  logged_in_status: "false",
});

export const isFlymyaSessionExpired = () => {
  try {
    const guestSession = localStorage.getItem("guest_flymya_session");

    if (
      guestSession &&
      guestSession !== "null" &&
      guestSession !== "undefined"
    ) {
      const guestSessionData = JSON.parse(guestSession);
      if (guestSessionData?.updatedAt) {
        const moment = require("moment");
        const updatedAtMoment = moment(
          guestSessionData.updatedAt,
          "YYYY-MM-DD HH:mm:ss",
        ).utcOffset(390);
        const now = moment().utcOffset(390);
        const differenceInMinutes = now.diff(updatedAtMoment, "minutes");
        return differenceInMinutes > 30; // 30 minutes expiry (same as FlymyaSessionHook)
      }
    }
    return false;
  } catch (error) {
    console.error("Error checking Flymya session expiry:", error);
    return false;
  }
};

export const initializeEventTrackerData = () => {
  try {
    const existingData = localStorage.getItem(EVENT_TRACKER_STORAGE_KEY);

    if (!existingData) {
      const initialData = getInitialTrackerData();
      localStorage.setItem(
        EVENT_TRACKER_STORAGE_KEY,
        JSON.stringify(initialData),
      );
    } else {
      // Keep legacy behavior; only migrate missing hotel object/payment stage.
      let parsedData = JSON.parse(existingData);
      let isUpdated = false;
      if (!parsedData.hotels) {
        parsedData.hotels = createEmptyStages();
        isUpdated = true;
      }
      if (parsedData.flights && parsedData.flights.payment === undefined) {
        parsedData.flights.payment = {};
        isUpdated = true;
      }
      if (parsedData.hotels && parsedData.hotels.payment === undefined) {
        parsedData.hotels.payment = {};
        isUpdated = true;
      }
      if (isUpdated) {
        localStorage.setItem(
          EVENT_TRACKER_STORAGE_KEY,
          JSON.stringify(parsedData),
        );
      }
    }
  } catch (error) {
    console.error("Error initializing event tracker data:", error);
  }
};

export const getEventTrackerData = () => {
  try {
    const data = localStorage.getItem(EVENT_TRACKER_STORAGE_KEY);

    if (data) {
      const parsed = JSON.parse(data);
      let next = parsed;
      let needsWrite = false;
      if (!next.hotels) {
        next = { ...next, hotels: createEmptyStages() };
        needsWrite = true;
      }
      if (!next.flights) {
        next = { ...next, flights: createEmptyStages() };
        needsWrite = true;
      }
      if (next.hotels && next.hotels.payment === undefined) {
        next = {
          ...next,
          hotels: { ...next.hotels, payment: {} },
        };
        needsWrite = true;
      }
      if (next.flights && next.flights.payment === undefined) {
        next = {
          ...next,
          flights: { ...next.flights, payment: {} },
        };
        needsWrite = true;
      }
      if (needsWrite) {
        localStorage.setItem(EVENT_TRACKER_STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    } else {
      initializeEventTrackerData();
      const newData = localStorage.getItem(EVENT_TRACKER_STORAGE_KEY);
      return newData ? JSON.parse(newData) : null;
    }
  } catch (error) {
    console.error("Error getting event tracker data:", error);
    // Try to initialize as fallback
    try {
      console.log(
        "Error occurred, attempting to initialize event tracker data...",
      );
      initializeEventTrackerData();
      const fallbackData = localStorage.getItem(EVENT_TRACKER_STORAGE_KEY);
      return fallbackData ? JSON.parse(fallbackData) : null;
    } catch (fallbackError) {
      console.error("Failed to initialize event tracker data:", fallbackError);
      return null;
    }
  }
};

// Update logged_in_status in localStorage
export const updateLoggedInStatus = () => {
  try {
    const currentData = getEventTrackerData();

    // If getEventTrackerData returns null, try to initialize manually
    if (!currentData) {
      initializeEventTrackerData();
      const newData = getEventTrackerData();
      if (!newData) {
        console.error(
          "Failed to initialize event tracker data in updateLoggedInStatus",
        );
        return;
      }
    }

    const dataToUse = currentData || getEventTrackerData();
    if (dataToUse) {
      const wasLoggedIn = dataToUse.logged_in_status === "true";
      const isNowLoggedIn = isAuthenticated();

      // If user was logged in and now logged out, reset user-related data
      if (wasLoggedIn && !isNowLoggedIn) {
        if (dataToUse.flights) {
          dataToUse.flights.customer_detail = {};
          delete dataToUse.flights.arranger_id;
          delete dataToUse.flights.reservation_id;
          dataToUse.flights.payment = {};
        }
        if (dataToUse.hotels) {
          dataToUse.hotels.customer_detail = {};
          delete dataToUse.hotels.arranger_id;
          delete dataToUse.hotels.reservation_id;
          dataToUse.hotels.payment = {};
        }
        delete dataToUse.reservation_id;
        KBZ_USER_FIELDS.forEach((key) => delete dataToUse[key]);

        localStorage.setItem(
          EVENT_TRACKER_STORAGE_KEY,
          JSON.stringify(dataToUse),
        );
      }

      // Update login status
      dataToUse.logged_in_status = isNowLoggedIn ? "true" : "false";
      localStorage.setItem(
        EVENT_TRACKER_STORAGE_KEY,
        JSON.stringify(dataToUse),
      );
    }
  } catch (error) {
    console.error("Error updating logged in status:", error);
  }
};

// The KBZPay customer's phone and nickname, kept at the root next to
// logged_in_status so every event can carry them. Cleared on logout.
export const KBZ_USER_FIELDS = ["PLAINTEXT_MOBILE_PHONE", "USER_NICKNAME"];

/**
 * Stores the phone and nickname from a KBZPay exchange (null when KBZPay did
 * not share one).
 */
export const updateKbzUserFields = ({ phone, name }) => {
  try {
    const currentData = getEventTrackerData();
    if (!currentData) return;
    currentData.PLAINTEXT_MOBILE_PHONE = phone ?? null;
    currentData.USER_NICKNAME = name ?? null;
    localStorage.setItem(
      EVENT_TRACKER_STORAGE_KEY,
      JSON.stringify(currentData),
    );
  } catch (error) {
    console.error("Error updating KBZ user fields:", error);
  }
};

/** The stored KBZ fields, leaving out any that were never set. */
export const getKbzUserFields = (data = getEventTrackerData()) =>
  KBZ_USER_FIELDS.reduce((fields, key) => {
    if (data && data[key] !== undefined) fields[key] = data[key];
    return fields;
  }, {});

/**
 * Reset event tracker data to initial state
 */
export const resetEventTrackerData = () => {
  try {
    const initialData = getInitialTrackerData();
    localStorage.setItem(
      EVENT_TRACKER_STORAGE_KEY,
      JSON.stringify(initialData),
    );
    return initialData;
  } catch (error) {
    console.error("Error resetting event tracker data:", error);
    return null;
  }
};

/**
 * Reset flight + hotel funnel stages when guest session is considered expired.
 * Preserves logged_in_status; clears legacy root reservation_id if present.
 */
export const resetFlightsTrackerStages = () => {
  try {
    let currentData = getEventTrackerData();
    if (!currentData) {
      initializeEventTrackerData();
      currentData = getEventTrackerData();
      if (!currentData) return null;
    }
    currentData.flights = createEmptyStages();
    currentData.hotels = createEmptyStages();
    delete currentData.reservation_id;
    localStorage.setItem(
      EVENT_TRACKER_STORAGE_KEY,
      JSON.stringify(currentData),
    );
    return currentData;
  } catch (error) {
    console.error("Error resetting flights tracker stages:", error);
    return null;
  }
};

/**
 * Reset one product funnel when the guest session is expired, before updating that
 * product's stage. Avoids wiping hotels when only flights.payment is updated (and vice versa).
 */
export const resetProductTrackerStages = (product) => {
  try {
    if (product !== "flights" && product !== "hotels") {
      return resetFlightsTrackerStages();
    }
    let currentData = getEventTrackerData();
    if (!currentData) {
      initializeEventTrackerData();
      currentData = getEventTrackerData();
      if (!currentData) return null;
    }
    currentData[product] = createEmptyStages();
    delete currentData.reservation_id;
    localStorage.setItem(
      EVENT_TRACKER_STORAGE_KEY,
      JSON.stringify(currentData),
    );
    return currentData;
  } catch (error) {
    console.error(`Error resetting ${product} tracker stages:`, error);
    return null;
  }
};

export const checkSessionAndUpdateStage = (
  product,
  stage,
  stageData,
  payloadData,
) => {
  try {
    if (isFlymyaSessionExpired()) {
      resetProductTrackerStages(product);
    }

    // update with new data
    const newData = updateProductStage(product, stage, stageData, payloadData);
    // console.log("newData", newData);
    return newData;
  } catch (error) {
    console.error(`Error in checkSessionAndUpdateStage:`, error);
  }
};

/**
 * Update any product stage data in localStorage
 * product - Product name (flights, hotels, etc.)
 * stage - Stage name (searching, choosing, customer_detail)
 * stageData - Data to store for the stage
 */
export const updateProductStage = (product, stage, stageData, payloadData) => {
  try {
    let currentData = getEventTrackerData();

    // If getEventTrackerData still returns null, try to initialize manually
    if (!currentData) {
      initializeEventTrackerData();
      currentData = getEventTrackerData();
    }

    if (currentData) {
      if (!currentData[product]) {
        currentData[product] = createEmptyStages();
      }

      // Check if the stage exists, if not, initialize it
      if (currentData[product][stage] === undefined) {
        currentData[product][stage] = {};
        localStorage.setItem(
          EVENT_TRACKER_STORAGE_KEY,
          JSON.stringify(currentData),
        );
      }

      // Update the current stage only
      currentData[product][stage] = {
        ...stageData,
      };

      // Update payload data if provided
      if (payloadData) {
        if (payloadData?.reservation_id) {
          const { reservation_id, ...restPayload } = payloadData;

          const { reservation_id: _existingReservationId, ...existingProduct } =
            currentData[product] || {};

          currentData[product] =
            Object.keys(restPayload).length > 0
              ? {
                  ...existingProduct,
                  ...restPayload,
                }
              : {
                  ...existingProduct,
                };

          currentData[product].reservation_id = reservation_id;
          delete currentData.reservation_id;
        } else {
          currentData[product] = {
            ...currentData[product],
            ...payloadData,
          };
        }
      }

      localStorage.setItem(
        EVENT_TRACKER_STORAGE_KEY,
        JSON.stringify(currentData),
      );
      return currentData;
    }

    console.error(`Invalid event tracker state for product: ${product}`);
  } catch (error) {
    console.error(`Error updating ${product}.${stage}:`, error);
  }
};

export const getEventTrackerDataByProduct = (product) => {
  const data = getEventTrackerData();
  if (!data || !data[product]) return data;

  const productPayload = {
    [product]: data[product],
    logged_in_status:
      data.logged_in_status !== undefined ? data.logged_in_status : "false",
    ...getKbzUserFields(data),
  };

  // Flights: some consumers expect legacy root reservation_id; hotels keep id only under hotels.*
  if (product === "flights") {
    const productRid = data.flights?.reservation_id ?? data.reservation_id;
    if (productRid !== undefined && productRid !== null && productRid !== "") {
      productPayload.reservation_id = productRid;
    }
  }

  return productPayload;
};

export const getSerializedTrackerPayloadByProduct = (product) => {
  const payload = getEventTrackerDataByProduct(product);
  return payload ? JSON.stringify(payload) : null;
};

/**
 * The rt/ow half of a flight event name. A reservation is a roundtrip when its
 * product type says so, or when it carries more than one flight leg.
 *
 * This decision was inlined as the same nested ternary at 102 call sites across
 * 21 files; only the suffix ever differed.
 */
/**
 * The hotel counterpart of {@link flightPayEvent}: fires a hotel payment event,
 * but only for a hotel reservation.
 *
 * The guard, the event name and the payload were written out identically at 40
 * call sites across 17 files; only the stage ("attempt" / "success") differed.
 */
export const trackHotelPayment = (resData, handleCreateUserAction, stage) => {
  if (resData?.type !== "Hotel") return;
  handleCreateUserAction({
    eventName: `hotel_short_payment_${stage}`,
    serializedPayload: getSerializedTrackerPayloadByProduct("hotels"),
  });
};

export const flightPayEvent = (flightInfo, suffix) => {
  const isRoundtrip =
    !!flightInfo &&
    (!!flightInfo?.[0]?.product_type?.name?.includes("roundtrip") ||
      flightInfo?.length > 1);
  return `flight_${isRoundtrip ? "rt" : "ow"}_${suffix}`;
};

const eventTracker = {
  flightPayEvent,
  trackHotelPayment,
  isFlymyaSessionExpired,
  initializeEventTrackerData,
  getEventTrackerData,
  getEventTrackerDataByProduct,
  getSerializedTrackerPayloadByProduct,
  updateProductStage,
  updateLoggedInStatus,
  resetEventTrackerData,
  resetFlightsTrackerStages,
  resetProductTrackerStages,
  checkSessionAndUpdateStage,
};

export default eventTracker;
